<template>
    <div class="widget-component">
        <v-container fluid>
            <v-container class="container-width">
                <v-row>
                    <v-col cols="12">
                        <v-card
                            :loading="loading"
                            class="mx-auto my-12"
                            :width="normalizedOptions.card.width"
                            :elevation="normalizedOptions.card.elevation"
                        >
                            <v-card-text>
                                <v-row
                                    align="center"
                                    class="mx-0"
                                >
                                    <v-col cols="12" class="px-16 py-16">
                                        <v-col cols="12">
                                            <h1 class="mb-6 color-primary">{{ normalizedOptions.title }}</h1>
                                        </v-col>

                                        <v-col cols="12" v-if="normalizedOptions.intro.show">
                                            <div v-html="normalizedOptions.intro.textHtml"></div>
                                        </v-col>

                                        <v-col cols="12">
                                            <label
                                                for="passwordField"
                                                v-if="!normalizedOptions.fields.password.inlineLabels"
                                                class="mb-4 font-weight-bold"
                                            >
                                                {{ normalizedOptions.fields.password.label }}
                                            </label>
                                            <v-text-field
                                                id="passwordField"
                                                v-model="password"
                                                :label="normalizedOptions.fields.password.inlineLabels ? normalizedOptions.fields.password.label : ''"
                                                outlined
                                                hide-details="auto"
                                                class="rounded-0"
                                                required
                                                type="password"
                                                :type="showPassword ? 'text' : 'password'"
                                                :append-icon="normalizedOptions.fields.password.enableShowPassword ? showPassword ? 'mdi-eye' : 'mdi-eye-off' : ''"
                                                @click:append="showPassword = !showPassword"
                                                :error-messages="getError('password')"
                                            ></v-text-field>
                                        </v-col>

                                        <v-col cols="12">
                                            <label
                                                for="passwordConfirmationField"
                                                v-if="!normalizedOptions.fields.passwordConfirmation.inlineLabels"
                                                class="mb-4 font-weight-bold"
                                            >
                                                {{ normalizedOptions.fields.passwordConfirmation.label }}
                                            </label>
                                            <v-text-field
                                                id="passwordConfirmationField"
                                                v-model="passwordConfirmation"
                                                :label="normalizedOptions.fields.passwordConfirmation.inlineLabels ? normalizedOptions.fields.passwordConfirmation.label : ''"
                                                outlined
                                                hide-details="auto"
                                                class="rounded-0"
                                                required
                                                type="password"
                                                :type="showPasswordConfirmation ? 'text' : 'password'"
                                                :append-icon="normalizedOptions.fields.passwordConfirmation.enableShowPassword ? showPasswordConfirmation ? 'mdi-eye' : 'mdi-eye-off' : ''"
                                                @click:append="showPasswordConfirmation = !showPasswordConfirmation"
                                                :error-messages="getError('password')"
                                            ></v-text-field>
                                        </v-col>

                                        <v-col cols="12" v-if="errors && errors.message">
                                            <v-alert
                                                prominent
                                                type="error"
                                            >
                                                <v-row align="center">
                                                    <v-col class="grow">
                                                        <p>
                                                            {{ errors.message }}
                                                        </p>
                                                        <p v-if="errors.message == 'Invalid token'">
                                                            Please re-start the password reset process.
                                                        </p>
                                                    </v-col>
                                                </v-row>
                                            </v-alert>
                                        </v-col>

                                        <v-col cols="12" md="8">
                                            <v-btn
                                                color="primary"
                                                @click="submit"
                                                class="ma-0"
                                                large
                                                block
                                                :loading="loading"
                                                :disabled="loading || !formValid"
                                            >
                                                {{ normalizedOptions.fields.submitButton.text }}
                                            </v-btn>
                                        </v-col>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>
        <v-dialog
            max-width="400"
            v-model="dialog"
        >
            <template v-slot:default="dialog">
                <v-card>
                    <v-toolbar
                        color="primary"
                        v-if="normalizedOptions.dialog.success.toolbar.show"
                    >
                        <span :style="{ 'color': normalizedOptions.dialog.success.toolbar.textColour }">
                            {{ normalizedOptions.dialog.success.toolbar.text }}
                        </span>
                    </v-toolbar>
                    <v-card-text>
                        <div class="pa-4">
                            <div v-html="normalizedOptions.dialog.success.textHtml"></div>
                        </div>
                    </v-card-text>
                    <v-card-actions class="justify-end">
                        <v-btn
                            v-if="normalizedOptions.dialog.success.buttons.close.show"
                            color="primary"
                            @click="closeDialog"
                        >
                            {{ normalizedOptions.dialog.success.buttons.close.text }}
                        </v-btn>
                        <v-btn
                            v-if="normalizedOptions.dialog.success.buttons.login.show"
                            color="primary"
                            :href="normalizedOptions.dialog.success.buttons.login.url"
                        >
                            {{ normalizedOptions.dialog.success.buttons.login.text }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </template>
        </v-dialog>
    </div>
</template>

<script>
import {defineComponent, ref, onMounted, computed, watch} from "@vue/composition-api";
import {has, isInteger, isNull, isNumber, merge} from "lodash";
import AuthenticationService from "../services/AuthenticationService";

export default defineComponent({
    name: "ForgotPasswordPasswordForm",
    components: {},
    props: {
        rootUrl: {
            type: String,
            required: true
        },
        settings: {
            type: Object
        },
        accessToken: {
            type: String
        },
    },
    setup(props) {
        const loading = ref(false);
        const errors = ref(null);
        const showPassword = ref(false);
        const showPasswordConfirmation = ref(false);
        const password = ref(null);
        const passwordConfirmation = ref(null);
        const formValid = ref(false);
        const dialog = ref(false);
        const defaultSettings = ref({
            title: 'Set your new password',
            intro: {
                show: true,
                textHtml: '<p>Please enter your new password.<br />Your password must be at least 6 characters long.</p>'
            },
            card: {
                width: 622,
                elevation: 5
            },
            fields: {
                password: {
                    inlineLabels: false,
                    label: 'New password*',
                    enableShowPassword: false,
                },
                passwordConfirmation: {
                    inlineLabels: false,
                    label: 'New password confirmation*',
                    enableShowPassword: false,
                },
                submitButton: {
                    text: 'Submit'
                }
            },
            dialog: {
                success: {
                    textHtml: '<p>All set!<br />' +
                        'Please wait while you are redirected to the login page...</p>',
                    toolbar: {
                        show: true,
                        text: 'Success',
                        textColour: 'white'
                    },
                    buttons: {
                        close: {
                            show: false,
                            text: 'Close'
                        },
                        login: {
                            show: true,
                            text: 'Login',
                            url: '/login',
                        }
                    }
                }
            },
            events: {
                'redirectAfterPasswordChange': '/login'
            }
        });

        const normalizedOptions = computed(() => {
            return merge(defaultSettings.value, props.settings);
        });

        const userId = computed(() => {
            const pathArray = window.location.pathname.split('/');
            if (isInteger(parseInt(pathArray[pathArray.length - 2]))) {
                return pathArray[pathArray.length - 2];
            }
            return null
        })

        const resetPasswordToken = computed(() => {
            const pathArray = window.location.pathname.split('/');
            // Check if reset password token length is 64 character long
            if (pathArray[pathArray.length - 1].length === 64) {
                return pathArray[pathArray.length - 1];
            }
            return null
        })

        function getError(field) {
            const fieldToSearch = 'errors.' + field + '[0]';
            if (has(errors.value, fieldToSearch)) {
                return errors.value.errors[field][0];
            }
            return null
        }

        function checkFormValid() {
            if (isNull(password.value)) {
                return false;
            }
            if (isNull(passwordConfirmation.value)) {
                return false;
            }
            if (password.value.length < 6) {
                return false;
            }
            if (passwordConfirmation.value.length < 6) {
                return false;
            }
            if (passwordConfirmation.value !== password.value) {
                return false;
            }
            // Later down the line maybe add additional checks regarding the userId, and the password token.
            return true;
        }

        function openDialog() {
            dialog.value = true;
        }

        function closeDialog() {
            dialog.value = false;
        }

        function redirectAfterPasswordChange() {
            if (has(normalizedOptions.value, 'events.redirectAfterPasswordChange')) {
                window.location.href = normalizedOptions.value.events.redirectAfterPasswordChange;
            }
        }

        function submit() {
            loading.value = true;
            errors.value = null;
            AuthenticationService.resetPassword(userId.value, resetPasswordToken.value, password.value)
                .then((response) => {
                    if (has(response, 'status')) {
                        if (response.status === 200) {
                            openDialog();
                            if (has(normalizedOptions.value, 'events.redirectAfterPasswordChange')) {
                                setTimeout(() => {
                                    redirectAfterPasswordChange();
                                }, 3000)
                            }
                        }
                    }
                })
                .catch(error => {
                    console.error(error);
                    if (has(error, 'response.data')) {
                        errors.value = error.response.data;
                    }
                })
                .finally(() => {
                    loading.value = false;
                });
        }

        function init() {
            loading.value = false;
        }

        watch([password, passwordConfirmation], () => {
            formValid.value = checkFormValid();
        });

        onMounted(() => {
            init();
        });

        return {
            // Variables
            normalizedOptions,
            loading,
            errors,
            password,
            passwordConfirmation,
            showPassword,
            showPasswordConfirmation,
            formValid,
            dialog,
            // Functions
            submit,
            getError,
            openDialog,
            closeDialog

        };
    }
});
</script>
